import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { X, Heart, AlertCircle } from 'lucide-react';
import Button from '../UI/Button';
import { publicAPI } from '../../services/api';
import { useAnimals } from '../../contexts/AnimalsContext';
import { useNotification } from '../../contexts/NotificationContext';

interface AdoptionApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  animalId: string;
  animalName: string;
}

type FormData = {
  adopterFirstName: string;
  adopterLastName: string;
  adopterEmail: string;
  adopterPhone: string;
  adopterAddress: string;
  livingArrangement: string;
  hasExperience: boolean;
  hasOtherPets: boolean;
  reasonForAdoption: string;
  additionalInfo: string;
};

const AdoptionApplicationModal: React.FC<AdoptionApplicationModalProps> = ({
  isOpen,
  onClose,
  animalId,
  animalName
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { refreshAnimals } = useAnimals();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await publicAPI.submitAdoptionApplication({
        animalId,
        adopterFirstName: data.adopterFirstName,
        adopterLastName: data.adopterLastName,
        adopterEmail: data.adopterEmail,
        adopterPhone: data.adopterPhone,
        adopterAddress: data.adopterAddress,
        livingArrangement: data.livingArrangement,
        hasExperience: data.hasExperience,
        hasOtherPets: data.hasOtherPets,
        reasonForAdoption: data.reasonForAdoption,
        additionalInfo: data.additionalInfo
      });

      reset();
      
      // Refresh animals list to remove the adopted animal
      await refreshAnimals();
      
      // Close modal
      onClose();
      
      // Show success notification
      addNotification({
        type: 'success',
        message: `Cererea de adopție pentru ${animalName} a fost înregistrată cu succes! Te vom contacta în curând pentru următorii pași.`,
        duration: 6000
      });
      
      // Navigate to home page
      navigate('/');
      
    } catch (error) {
      console.error('Error submitting adoption application:', error);
      setSubmitError('A apărut o eroare la trimiterea cererii de adopție. Te rugăm să încerci din nou.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      reset();
      setSubmitError(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-primary">
            Cerere de adopție pentru {animalName}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          {submitError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-start">
              <AlertCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
              <p>{submitError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label htmlFor="adopterFirstName" className="block text-gray-700 font-medium mb-1">
                Prenume *
              </label>
              <input
                id="adopterFirstName"
                type="text"
                className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  errors.adopterFirstName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Prenumele tău"
                {...register('adopterFirstName', { 
                  required: 'Prenumele este obligatoriu',
                  minLength: { value: 2, message: 'Prenumele trebuie să aibă cel puțin 2 caractere' }
                })}
              />
              {errors.adopterFirstName && (
                <p className="text-red-500 text-sm mt-1">{errors.adopterFirstName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="adopterLastName" className="block text-gray-700 font-medium mb-1">
                Nume *
              </label>
              <input
                id="adopterLastName"
                type="text"
                className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  errors.adopterLastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Numele tău"
                {...register('adopterLastName', { 
                  required: 'Numele este obligatoriu',
                  minLength: { value: 2, message: 'Numele trebuie să aibă cel puțin 2 caractere' }
                })}
              />
              {errors.adopterLastName && (
                <p className="text-red-500 text-sm mt-1">{errors.adopterLastName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label htmlFor="adopterEmail" className="block text-gray-700 font-medium mb-1">
                Email *
              </label>
              <input
                id="adopterEmail"
                type="email"
                className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  errors.adopterEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Email-ul tău"
                {...register('adopterEmail', { 
                  required: 'Email-ul este obligatoriu',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Adresa de email este invalidă'
                  }
                })}
              />
              {errors.adopterEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.adopterEmail.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="adopterPhone" className="block text-gray-700 font-medium mb-1">
                Telefon *
              </label>
              <input
                id="adopterPhone"
                type="tel"
                className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  errors.adopterPhone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Numărul tău de telefon"
                {...register('adopterPhone', { 
                  required: 'Telefonul este obligatoriu',
                  pattern: {
                    value: /^[0-9+\-\s()]+$/,
                    message: 'Numărul de telefon nu este valid'
                  }
                })}
              />
              {errors.adopterPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.adopterPhone.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-medium mb-3">
                Ai experiență cu câinii? *
              </label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="true"
                    className="mr-2"
                    {...register('hasExperience', { required: 'Selectează o opțiune' })}
                  />
                  <span>Da, am experiență cu câinii</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="false"
                    className="mr-2"
                    {...register('hasExperience', { required: 'Selectează o opțiune' })}
                  />
                  <span>Nu, acesta va fi primul meu câine</span>
                </label>
              </div>
              {errors.hasExperience && (
                <p className="text-red-500 text-sm mt-1">{errors.hasExperience.message}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-3">
                Ai alte animale de companie? *
              </label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="true"
                    className="mr-2"
                    {...register('hasOtherPets', { required: 'Selectează o opțiune' })}
                  />
                  <span>Da, am alte animale</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="false"
                    className="mr-2"
                    {...register('hasOtherPets', { required: 'Selectează o opțiune' })}
                  />
                  <span>Nu, nu am alte animale</span>
                </label>
              </div>
              {errors.hasOtherPets && (
                <p className="text-red-500 text-sm mt-1">{errors.hasOtherPets.message}</p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="adopterAddress" className="block text-gray-700 font-medium mb-1">
              Adresa completă *
            </label>
            <textarea
              id="adopterAddress"
              rows={2}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.adopterAddress ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Strada, numărul, oraș, județ"
              {...register('adopterAddress', { 
                required: 'Adresa este obligatorie',
                minLength: { value: 10, message: 'Adresa trebuie să fie completă' }
              })}
            />
            {errors.adopterAddress && (
              <p className="text-red-500 text-sm mt-1">{errors.adopterAddress.message}</p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="livingArrangement" className="block text-gray-700 font-medium mb-1">
              Tipul locuinței *
            </label>
            <select
              id="livingArrangement"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.livingArrangement ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('livingArrangement', { required: 'Selectează tipul locuinței' })}
            >
              <option value="">Selectează tipul locuinței</option>
              <option value="apartment">Apartament</option>
              <option value="house_no_yard">Casă fără curte</option>
              <option value="house_with_yard">Casă cu curte</option>
              <option value="farm">Fermă/Proprietate rurală</option>
            </select>
            {errors.livingArrangement && (
              <p className="text-red-500 text-sm mt-1">{errors.livingArrangement.message}</p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="reasonForAdoption" className="block text-gray-700 font-medium mb-1">
              De ce vrei să adopți pe {animalName}? *
            </label>
            <textarea
              id="reasonForAdoption"
              rows={3}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.reasonForAdoption ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Spune-ne motivele pentru care vrei să adopți acest câine..."
              {...register('reasonForAdoption', { 
                required: 'Motivul adopției este obligatoriu',
                minLength: { value: 20, message: 'Descrierea trebuie să aibă cel puțin 20 de caractere' }
              })}
            />
            {errors.reasonForAdoption && (
              <p className="text-red-500 text-sm mt-1">{errors.reasonForAdoption.message}</p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="additionalInfo" className="block text-gray-700 font-medium mb-1">
              Informații suplimentare
            </label>
            <textarea
              id="additionalInfo"
              rows={2}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Orice altă informație pe care vrei să o împărtășești cu noi..."
              {...register('additionalInfo')}
            />
          </div>

          <div className="bg-accent-100 p-4 rounded-lg mb-6">
            <p className="text-sm text-accent-dark">
              <strong>Notă:</strong> După trimiterea acestei cereri, un reprezentant al adăpostului te va contacta 
              pentru a discuta următorii pași. Procesul de adopție poate include o vizită la adăpost și 
              o eventuală verificare a condițiilor de locuire.
            </p>
          </div>

          <div className="mb-6">
            <p className="text-xs text-gray-600 text-center">
              Trimiterea formularului denotă acceptul cu termenii și condițiile platformei.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Anulează
            </Button>
            <Button
              type="submit"
              variant="accent"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Se trimite...' : 'Trimite cererea de adopție'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdoptionApplicationModal; 